// Persistent bottom toast that appears on every screen (except /anforderung
// itself) and routes the visitor to the sticker request page. The dismissal
// is intentionally in-memory only — every page reload brings the toast back.
import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Sticker, X } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useQr } from "../lib/qrContext";

export const StickerToast = () => {
    const { t } = useI18n();
    const { pathname } = useLocation();
    const { qrId } = useQr();
    const [dismissed, setDismissed] = useState(false);

    // The toast is a soft nudge for visitors who arrived *without* a
    // personalised QR link – they're the ones who might still order a
    // sticker. If a ?id= is already in the URL the visitor is presumed
    // to be scanning an existing sticker, so we hide the toast.
    if (qrId) return null;
    // Hide on the request page itself; the visitor is already there.
    if (pathname.startsWith("/anforderung")) return null;
    if (dismissed) return null;

    const dismiss = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDismissed(true);
    };

    return (
        <Link
            to="/anforderung"
            data-testid="sticker-toast"
            className="fixed z-[1500] group bg-emerald-600 hover:bg-emerald-700 text-white shadow-2xl transition-all
                       /* Mobile: thin pill at the bottom */
                       bottom-3 left-3 right-3 rounded-2xl pl-3 pr-2 py-2.5 flex items-center gap-3
                       /* Tablet+: square card pinned bottom-right */
                       sm:left-auto sm:right-5 sm:bottom-5 sm:w-64 sm:h-64 sm:rounded-3xl sm:p-5 sm:flex-col sm:items-start sm:justify-between sm:gap-0"
        >
            <button
                data-testid="sticker-toast-dismiss"
                onClick={dismiss}
                aria-label="Schließen"
                className="absolute top-2 right-2 hidden sm:flex w-8 h-8 rounded-full bg-white/10 hover:bg-white/25 text-white items-center justify-center transition"
            >
                <X className="w-4 h-4" strokeWidth={2.5} />
            </button>

            {/* Icon – small inline on mobile, large block on desktop */}
            <div className="w-9 h-9 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-white/15 flex items-center justify-center flex-shrink-0">
                <Sticker className="w-5 h-5 sm:w-7 sm:h-7" />
            </div>

            {/* Text */}
            <div className="text-sm sm:text-[17px] sm:leading-tight font-display font-bold leading-snug flex-1 sm:flex-none min-w-0 sm:min-w-full">
                {t("sticker_toast_text")}
            </div>

            {/* Mobile dismiss (inline) */}
            <button
                data-testid="sticker-toast-dismiss-mobile"
                onClick={dismiss}
                aria-label="Schließen"
                className="sm:hidden w-7 h-7 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition flex-shrink-0"
            >
                <X className="w-4 h-4" strokeWidth={2.5} />
            </button>

            {/* Desktop CTA arrow */}
            <div className="hidden sm:flex items-center gap-1.5 font-display font-bold text-sm uppercase tracking-wider opacity-90 group-hover:opacity-100 transition">
                <span>{t("sticker_toast_cta")}</span>
                <span aria-hidden>→</span>
            </div>
        </Link>
    );
};
